import { Component, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { map } from 'rxjs/operators';
import { DatatableColumn } from 'projects/bis-shared/src/lib/datatable/datatable-column';
import { DatatableColumnWidth } from 'projects/bis-shared/src/lib/datatable/datatable-column-width';
import { FilterDataType } from 'projects/bis-shared/src/lib/datatable/datatable-filter/filter-operations';
import { DatatableSort } from 'projects/bis-shared/src/lib/datatable/datatable-sort';
import { DatatableStaticTemplateType } from 'projects/bis-shared/src/lib/datatable/datatable-static-template';
import { IDynamicFormGroupDefinition } from 'projects/bis-shared/src/lib/dynamic-forms/models/dynamic-form-group';
import { ISelectOption } from 'projects/bis-shared/src/lib/dynamic-forms/models/select-option';
import { ISelectOptionGroup } from 'projects/bis-shared/src/lib/dynamic-forms/models/select-option-group';
import { DynamicFormsService } from 'projects/bis-shared/src/lib/dynamic-forms/services/dynamic-forms/dynamic-forms.service';
import { ObjectSubTypeService } from 'projects/bis-shared/src/lib/reference-data/object-sub-type.service';
import { ReferenceDataService } from 'projects/bis-shared/src/lib/reference-data/reference-data.service';
import { ExternalApiUrls } from 'projects/bis-shared/src/lib/utility/apiurls-external';
import { AppUrls } from 'projects/bis-shared/src/lib/utility/appurls';
import { SelectHelpers } from 'projects/bis-shared/src/lib/utility/select-helpers';
import { ObjectTypeCode } from 'projects/bis-shared/src/lib/utility/system-type-constants';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { PriorAuthSearch } from './prior-auth-search';
import { UntypedFormGroup } from '@angular/forms';
import { DatatableComponent } from 'projects/bis-shared/src/lib/datatable/datatable/datatable.component';
import { MaskUtility } from 'projects/bis-shared/src/lib/utility/mask-utility';
import { EntityIdFormatterService } from 'projects/bis-shared/src/lib/utility/entity-id-formatter.service';
import { CommonModule } from '@angular/common';
import { DynamicFormsModule } from 'projects/bis-shared/src/lib/dynamic-forms/dynamic-forms.module';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DatatableModule } from 'projects/bis-shared/src/lib/datatable/datatable.module';
import { EntityFormatterPipe } from 'projects/bis-shared/src/lib/utility/entity-formatter.pipe';
import { RouterModule } from '@angular/router';

@Component({
  selector: 'app-prior-auth-search-component',
  templateUrl: './prior-auth-search.component.html',
  styleUrls: ['./prior-auth-search.component.scss'],
  imports: [
    RouterModule,
    CommonModule,
    DynamicFormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    DatatableModule,
    EntityFormatterPipe,
  ],
})
export class PriorAuthSearchComponent implements OnInit {
  private externalApiUrls = inject(ExternalApiUrls);
  private dynamicFormsService = inject(DynamicFormsService);
  private refDataService = inject(ReferenceDataService);
  private objectSubTypeService = inject(ObjectSubTypeService);
  private entityIdFormatter = inject(EntityIdFormatterService);

  @ViewChild('idTemplate', { static: true }) idTemplate: TemplateRef<any>;
  @ViewChild(DatatableComponent) datatableComponent: DatatableComponent;

  AppUrls = AppUrls;
  columns: DatatableColumn[];
  dataUrl: string;
  defaultSort: DatatableSort[];
  hasSearched: boolean = false;
  priorAuthTypeCode = ObjectTypeCode.PriorAuthorization;
  parSearchTypes$: Observable<ISelectOptionGroup[]>;
  isCustomSearchVisible: boolean = false;
  formInfo: IDynamicFormGroupDefinition;
  parSearchForm: UntypedFormGroup;
  objectStatuses$: BehaviorSubject<ISelectOptionGroup[]> = new BehaviorSubject([]);
  priorAuthRequestTypes$: BehaviorSubject<ISelectOptionGroup[]> = new BehaviorSubject([]);
  searchCriteria = new PriorAuthSearch();
  priorAuthObjectTypeCode: string = ObjectTypeCode.PriorAuthorization;
  priorAuthIdMaskConfig = MaskUtility.getEntityIdMask(this.priorAuthObjectTypeCode);
  dataTableLimit: number = 10000;
  hasSearchInProgress: boolean = false;

  
 

  constructor() {
    this.defaultSort = [
      {
        property: 'parStatusDate',
        sortAscending: false,
      },
    ];

    this.parSearchTypes$ = of(
      SelectHelpers.toSelectList(
        [
          {
            name:
              'All Inflight PARs - Up to 10,000 most recent inflight PAR records with statuses updated in the last 30 days are available.',
            value: 1,
            sort: 1,
          },
          {
            name:
              'All Closed PARs - Up to 10,000 most recent closed PAR records with statuses updated in the last 30 days are available.',
            value: 2,
            sort: 2,
          },
          {
            name:
              'Custom PAR Search - Custom search allows the selection of specific criteria to retrieve desired results.',
            value: 3,
            sort: 3,
          },
        ],
        entity => entity.name,
        entity => entity.value,
        entity => entity.sort,
      ),
    );
  }

  ngOnInit(): void {
    this.setColumns();
    this.dataUrl = this.externalApiUrls.priorAuthorization.priorAuthSearch();

    this.dynamicFormsService
      .getFormGroupForModel('priorAuthorizationRequestExternalSearchForm')
      .subscribe(formInfo => {
        this.formInfo = formInfo;
        this.parSearchForm = formInfo.formGroup;
      });

    this.refDataService
      .getReferenceData('ObjectStatus')
      .pipe(
        map((optionGroups: ISelectOptionGroup[]) => {
          optionGroups[0].options.forEach(objectStatus => {
            if (objectStatus.code !== ObjectTypeCode.PriorAuthorization) {
              objectStatus.isDeleted = true;
            }
          });
          return optionGroups;
        }),
      )
      .subscribe(refData => this.objectStatuses$.next(refData));

    this.objectSubTypeService
      .getObjectSubTypes(ObjectTypeCode.PriorAuthorization)
      .pipe(map((optionGroups: ISelectOptionGroup[]) => optionGroups))
      .subscribe(refData => this.priorAuthRequestTypes$.next(refData));
  }

  setColumns() {
    this.columns = [
      {
        title: 'PAR ID',
        property: 'priorAuthorizationId',
        filterDataType: FilterDataType.SystemId,
        filterSystemIdFixedTypeCode: ObjectTypeCode.PriorAuthorization,
        template: this.idTemplate,
      },
      {
        title: 'Type',
        property: 'parRequestType',
        filterDataType: FilterDataType.Reference,
      },
      {
        title: 'PAR Status',
        property: 'objectStatusName',
        filterDataType: FilterDataType.Reference,
      },
      {
        title: 'Submission Date',
        property: 'submissionDate',
        filterDataType: FilterDataType.Date,
        template: DatatableStaticTemplateType.EasternDateTemplate,
        width: DatatableColumnWidth.large,
      },
      {
        title: 'PAR Status Date',
        property: 'parStatusDate',
        filterDataType: FilterDataType.Date,
        template: DatatableStaticTemplateType.EasternDateTemplate,
        width: DatatableColumnWidth.large,
      },
      {
        title: 'WCB Case #',
        property: 'wcbCaseNumber',
        filterDataType: FilterDataType.String,
        template: DatatableStaticTemplateType.CaseNumberTemplate,
        width: DatatableColumnWidth.large,
      },
      {
        title: 'Patient',
        property: 'claimantName',
        filterDataType: FilterDataType.String,
      },
      { title: 'Provider', property: 'providerName', filterDataType: FilterDataType.String },
      {
        title: 'Insurer ID',
        property: 'insurerId',
        sortProperties: ['wNumber'],
        filterDataType: FilterDataType.String,
      },
      {
        title: 'Claim Admin Claim #',
        property: 'claimAdminClaimNumber',
        filterDataType: FilterDataType.String,
      },
    ];
  }

  receiveSelectedParSearchType(searchType: ISelectOption[]) {
    this.hasSearched = false;
    this.isCustomSearchVisible = searchType?.[0]?.value === 3;
    this.searchCriteria = new PriorAuthSearch();

    //clear out all form values besides the par search type
    this.parSearchForm.patchValue({
      priorAuthorizationRequestTypeId: null,
      priorAuthorizationId: null,
      priorAuthSystemId: null,
      objectStatusId: null,
      wCBCaseNumber: null,
      parSubmissionDateFrom: null,
      parSubmissionDateTo: null,
      parStatusDateFrom: null,
      parStatusDateTo: null,
      claimantLastName: null,
      providerName: null,
      claimAdminClaimNumber: null,
      insurerNumber: null,
    });
  }

  resetForm() {
    this.parSearchForm.reset();
    this.hasSearched = false;
    this.isCustomSearchVisible = false;
    this.searchCriteria = new PriorAuthSearch();
    this.datatableComponent.resetFilters();
    this.datatableComponent.clearData();
  }

  search() {
    if (!this.parSearchForm.valid) {
      return;
    }
    this.hasSearchInProgress = true;
    this.datatableComponent.clearData();
    this.searchCriteria = this.parSearchForm.value;
    this.retrievePriorAuthIdFromSystemId();

    if (this.parSearchForm.value.claimantLastName === '') {
      this.parSearchForm.value.claimantLastName = null;
    }
    if (this.parSearchForm.value.providerName === '') {
      this.parSearchForm.value.providerName = null;
    }
    if (this.parSearchForm.value.claimAdminClaimNumber === '') {
      this.parSearchForm.value.claimAdminClaimNumber = null;
    }
    if (this.parSearchForm.value.insurerNumber === '') {
      this.parSearchForm.value.insurerNumber = null;
    }
    if (this.parSearchForm.value.wCBCaseNumber === '') {
      this.parSearchForm.value.wCBCaseNumber = null;
    }

    this.datatableComponent.currentPage = 1;
    this.hasSearched = true;

    setTimeout(() => this.datatableComponent.reloadTable(), 0);
  }

  enableSearchButton() {
    this.hasSearchInProgress = false;
  }

  retrievePriorAuthIdFromSystemId() {
    if (this.searchCriteria.priorAuthSystemId) {
      const systemPriorAuthIdNoDashes = this.entityIdFormatter.getNumericId(
        this.searchCriteria.priorAuthSystemId,
      );
      const numericPriorAuthId = Number(systemPriorAuthIdNoDashes);
      this.searchCriteria.priorAuthSystemId = this.entityIdFormatter.getFormattedId(
        numericPriorAuthId,
        'PA',
      );
      this.searchCriteria.priorAuthorizationId = numericPriorAuthId;
    } else {
      this.searchCriteria.priorAuthorizationId = null;
    }
  }

  onPriorAuthSystemIdChanged(value) {
    if (value === 'PA-') {
      this.parSearchForm.get('priorAuthSystemId').reset();
    }
  }

  updateParIdField(event) {
    if (
      event.type === 'keydown' &&
      event.code === 'Backspace' &&
      !this.parSearchForm.get('priorAuthSystemId').value
    ) {
      this.parSearchForm.get('priorAuthSystemId').reset();
    }
  }
}
